
import React, { useEffect, useState } from 'react';
import { useSession } from '../context/SessionContext';
import { t } from '../services/sessionManager';
import { UserService } from '../services/db';
import { LocalUser, Language } from '../types';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../constants';
import { Badge } from '../components/ui/Badge';
import { Loader } from '../components/ui/Loader';

const Admin: React.FC = () => {
  const { user, language, isAuthenticated, isLoading, setLanguage } = useSession();
  const [localUsers, setLocalUsers] = useState<LocalUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Client-side security check
    if (!isLoading && (!isAuthenticated || !user?.isAdmin)) {
      navigate(`/${language}`);
    }
  }, [isAuthenticated, user, isLoading, navigate, language]);

  useEffect(() => {
    if (user?.isAdmin) {
      loadUsers();
    }
  }, [user]);

  const loadUsers = () => {
    setLoadingUsers(true);
    UserService.getAllUsers().then((data) => {
      setLocalUsers(data);
      setLoadingUsers(false);
    });
  };

  const handleUpdate = async (userId: string, updates: { isAdmin?: boolean; language?: string }) => {
    setUpdatingId(userId);
    const updatedUser = await UserService.updateUser(userId, updates);
    if (updatedUser) {
      setLocalUsers(prev => prev.map(u => u.id === userId ? updatedUser : u));
      
      // UX Improvement: If Admin updates their own language, apply it globally immediately
      // The LanguageWrapper will detect this change and redirect the URL automatically
      if (userId === user?.id && updates.language) {
        setLanguage(updates.language as Language);
      }
    }
    setUpdatingId(null);
  };

  const filteredUsers = localUsers.filter(u => 
    u.maskedEmail.toLowerCase().includes(searchTerm.toLowerCase()) || 
    u.id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!user?.isAdmin) return null;

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-slate-400">
        <button onClick={() => navigate(`/${language}${ROUTES.DASHBOARD}`)} className="hover:text-gray-900 dark:hover:text-white hover:underline">
          {t('dashboard', language)}
        </button>
        <span>/</span>
        <span className="text-gray-900 dark:text-white font-medium">{t('adminPanel', language)}</span>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t('usersList', language)}</h1>
        <div className="relative">
          <input
            type="text"
            placeholder={t('searchPlaceholder', language)}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full sm:w-64 pl-10 pr-4 py-2 border border-gray-300 dark:border-slate-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-slate-900 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500"
          />
          <svg className="absolute left-3 top-2.5 h-4 w-4 text-gray-400 dark:text-slate-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800 overflow-hidden transition-colors duration-200">
        {loadingUsers ? (
          <div className="p-8">
            <Loader language={language} />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-800">
              <thead className="bg-gray-50 dark:bg-slate-800/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                    {t('email', language)} / {t('id', language)}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                    {t('role', language)}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                    {t('languageLabel', language)}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                    {t('lastLogin', language)}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                    {t('actions', language)}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-slate-900 divide-y divide-gray-200 dark:divide-slate-800">
                {filteredUsers.length > 0 ? filteredUsers.map((u) => (
                  <tr key={u.id} className="hover:bg-gray-50 dark:hover:bg-slate-800/30 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="font-medium text-gray-900 dark:text-white">{u.maskedEmail}</div>
                      <div className="text-gray-400 dark:text-slate-500 text-xs font-mono mt-0.5">{u.id}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <button 
                        onClick={() => handleUpdate(u.id, { isAdmin: !u.isAdmin })}
                        disabled={updatingId === u.id || u.id === user.id} // Prevent removing own admin
                        className={`transition-opacity ${updatingId === u.id ? 'opacity-50' : 'opacity-100'}`}
                        title={u.id === user.id ? t('cannotChangeOwnRole', language) : t('toggleAdmin', language)}
                      >
                         <Badge variant={u.isAdmin ? 'danger' : 'success'}>
                           {u.isAdmin ? t('admin', language) : t('user', language)}
                        </Badge>
                      </button>
                    </td>
                     <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <select 
                        value={u.language}
                        onChange={(e) => handleUpdate(u.id, { language: e.target.value })}
                        disabled={updatingId === u.id}
                        className="text-xs border-gray-200 dark:border-slate-700 rounded-md focus:ring-primary focus:border-primary cursor-pointer uppercase bg-transparent dark:bg-slate-900 dark:text-white py-1 px-2"
                      >
                        {Object.values(Language).map((lang) => (
                          <option key={lang} value={lang}>{lang}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-slate-400">
                      {new Date(u.lastLoginAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {/* Placeholder for future specific edit actions if needed */}
                      <span className="text-gray-300 dark:text-slate-600">--</span>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center text-gray-500 dark:text-slate-500 text-sm">
                      {t('noUsersFound', language)} "{searchTerm}"
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
